<template>
  <div class="manage-menu manage-menu--edit">
    <header class="manage-menu__header">
      <div class="manage-menu__header-inner">
        <h2 class="manage-menu__title">Manage Menu</h2>
      </div>
    </header>

    <div class="manage-menu__body">
      <div class="manage-menu__left">
        <div class="manage-menu__list">
          <menu-tree
            :items="menuItems"
            :parent="null"
            :depth="0"
            :expanded-ids="expandedIds"
            :handlers="treeHandlers"
          />
        </div>
      </div>

      <div class="manage-menu__right">
        <div class="manage-menu__panel">
          <h3 class="manage-menu__panel-title">{{ panelTitle }}</h3>
          <div class="manage-menu__panel-body">
            <div class="manage-menu__form-group">
              <label class="manage-menu__form-label">Item type</label>
              <div class="manage-menu__form-checkboxes">
                <label class="manage-menu__form-checkbox">
                  <input
                    v-model="menuItemForm.isDropdown"
                    type="checkbox"
                  >
                  <span>Item is a dropdown menu</span>
                </label>
                <label class="manage-menu__form-checkbox">
                  <input
                    v-model="menuItemForm.isSubmenu"
                    type="checkbox"
                    :disabled="addSubItemParent != null"
                  >
                  <span>Item belongs to a submenu</span>
                </label>
              </div>
            </div>
            <div v-if="parentMenuVisible" class="manage-menu__form-group">
              <label class="manage-menu__form-label">Parent menu</label>
              <div v-if="isParentMenuLocked" class="manage-menu__parent-fixed">{{ parentMenuDisplay }}</div>
              <el-select
                v-else
                v-model="menuItemForm.parentId"
                placeholder="Select parent menu"
                class="manage-menu__form-select full-width"
              >
                <el-option
                  v-for="parent in dropdownParents"
                  :key="parent.id"
                  :label="parent.label"
                  :value="parent.id"
                />
              </el-select>
            </div>
            <div class="manage-menu__form-group">
              <label class="manage-menu__form-label">Text (SRB)</label>
              <el-input
                v-model="menuItemForm.labelSrb"
                placeholder="Text (SRB)"
                class="manage-menu__form-input"
              />
            </div>
            <div class="manage-menu__form-group">
              <label class="manage-menu__form-label">Text (ENG)</label>
              <el-input
                v-model="menuItemForm.labelEng"
                placeholder="Text (ENG)"
                class="manage-menu__form-input"
              />
            </div>
            <div class="manage-menu__form-group">
              <label class="manage-menu__form-label">Link leads to</label>
              <el-select
                v-model="menuItemForm.linkType"
                placeholder="Select"
                class="manage-menu__form-select full-width"
              >
                <el-option label="Link leads to a page" value="page" />
                <el-option label="Link leads to external site" value="external" />
              </el-select>
            </div>
            <div v-if="menuItemForm.linkType === 'page'" class="manage-menu__form-group">
              <label class="manage-menu__form-label">Page</label>
              <el-select
                v-model="menuItemForm.pathOrUrl"
                placeholder="Select a page"
                class="manage-menu__form-select full-width"
              >
                <el-option
                  v-for="page in pages"
                  :key="page.id"
                  :label="pageTitle(page)"
                  :value="pagePath(page)"
                />
              </el-select>
            </div>
            <div v-if="menuItemForm.linkType === 'external'" class="manage-menu__form-group">
              <label class="manage-menu__form-label">Link / URL</label>
              <el-input
                v-model="menuItemForm.pathOrUrl"
                placeholder="https://..."
                class="manage-menu__form-input"
              />
            </div>
            <div class="manage-menu__form-group">
              <label class="manage-menu__form-checkbox manage-menu__form-checkbox--standalone">
                <input v-model="menuItemForm.openInNewTab" type="checkbox">
                <span>Open in a new tab</span>
              </label>
            </div>
          </div>
          <div class="manage-menu__panel-footer">
            <el-button class="manage-menu__btn-primary" @click="goBack">Cancel</el-button>
            <el-button
              class="manage-menu__btn-save"
              :disabled="!hasValidLabel"
              @click="saveMenuItem"
            >
              <span class="material-symbols-outlined manage-menu__btn-icon">check</span>
              Save
            </el-button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { Input, Select, Option, Button, Message } from 'element-ui'
import MenuTree from '@/components/MenuTree'

function getDefaultMenuItemForm () {
  return {
    isDropdown: false,
    isSubmenu: false,
    parentId: null,
    labelSrb: '',
    labelEng: '',
    linkType: 'page',
    pathOrUrl: '',
    openInNewTab: false
  }
}

export default {
  name: 'ManageMenuEdit',

  components: {
    MenuTree,
    ElInput: Input,
    ElSelect: Select,
    ElOption: Option,
    ElButton: Button
  },

  data () {
    return {
      dragSource: null,
      formMode: 'add',
      addSubItemParent: null,
      editingItem: null,
      editingParentIndex: null,
      editingChildIndex: null,
      editingParent: null,
      menuItemForm: getDefaultMenuItemForm()
    }
  },

  computed: {
    ...mapState('menu', ['menuItems', 'expandedIds']),
    ...mapGetters('menu', ['unsavedCount']),
    ...mapState('pages', { pagesData: 'data' }),

    pages () {
      return Array.isArray(this.pagesData) ? this.pagesData : []
    },

    siteUrl () {
      if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOCAL_FRONTEND) {
        return process.env.VUE_APP_LOCAL_FRONTEND.replace(/\/$/, '')
      }
      return typeof window !== 'undefined' ? 'https://mondial90.rs' : '#'
    },

    panelTitle () {
      return this.formMode === 'edit' ? 'Edit menu item' : 'Add item to menu'
    },

    treeHandlers () {
      return {
        canEdit: (item) => this.canEditItem(item),
        canDelete: (item) => !this.isProtectedMenuItem(item),
        toggleExpand: (id) => this.toggleExpand(id),
        edit: (item) => this.goToEdit(item),
        addSub: (item) => this.goToAddSubItem(item),
        remove: (parent, index) => this.deleteItem(parent, index),
        isDragging: (parent, index) => this.dragSource != null &&
          this.dragSource.parent === parent && this.dragSource.index === index,
        dragStart: (e, parent, index) => this.onDragStart(e, parent, index),
        dragEnd: () => this.onDragEnd(),
        dragOver: (e) => this.onDragOver(e),
        drop: (parent, index) => this.onDrop(parent, index)
      }
    },

    dropdownParents () {
      const descendantIds = (item) => {
        if (!item) return new Set()
        const ids = new Set([item.id])
        if (Array.isArray(item.children)) {
          for (const child of item.children) {
            for (const id of descendantIds(child)) ids.add(id)
          }
        }
        return ids
      }
      const excludeIds = this.formMode === 'edit' && this.editingItem
        ? descendantIds(this.editingItem)
        : new Set()
      const collect = (items, parentLabels) => {
        const result = []
        for (const item of items) {
          if (!item.isDropdown) continue
          if (excludeIds.has(item.id)) continue
          const labels = [...parentLabels, item.label || '']
          const label = labels.filter(Boolean).join(' / ')
          result.push({ id: item.id, label: label || item.id })
          if (Array.isArray(item.children)) {
            result.push(...collect(item.children, labels))
          }
        }
        return result
      }
      return collect(this.menuItems, [])
    },

    hasValidLabel () {
      return (this.menuItemForm.labelSrb && this.menuItemForm.labelSrb.trim()) ||
        (this.menuItemForm.labelEng && this.menuItemForm.labelEng.trim())
    },

    effectiveParent () {
      if (this.addSubItemParent) return this.addSubItemParent
      if (this.menuItemForm.isSubmenu && this.menuItemForm.parentId) {
        const found = this.findItemById(this.menuItemForm.parentId)
        return found ? found.item : null
      }
      return null
    },

    parentMenuVisible () {
      return (this.formMode === 'edit' && (this.editingParent || this.menuItemForm.isSubmenu)) ||
        (this.formMode === 'add' && this.menuItemForm.isSubmenu)
    },

    isParentMenuLocked () {
      return this.formMode === 'add' && this.addSubItemParent != null
    },

    parentMenuDisplay () {
      const item = this.formMode === 'edit' ? this.editingParent : this.addSubItemParent
      if (!item) return ''
      const labels = this.getHierarchyLabels(item)
      return labels.length ? labels.join(' / ') : (item.label || '')
    }
  },

  async created () {
    await Promise.all([
      this.$store.dispatch('menu/fetchMenu'),
      this.$store.dispatch('pages/load')
    ])
    if (this.$route.name === 'ManageMenuEdit' && this.$route.params.id) {
      this.initEditMode(this.$route.params.id)
    } else if (this.$route.name === 'ManageMenuAdd') {
      this.initAddMode(this.$route.query.parentId || null)
    }
  },

  watch: {
    async '$route' (to) {
      // Capture route data before the async fetch to prevent race conditions
      // where a later navigation changes this.$route before we read it.
      const toName = to.name
      const toId = (to.params && to.params.id) || null
      const toParentId = (to.query && to.query.parentId) || null
      await this.$store.dispatch('menu/fetchMenu')
      if (toName === 'ManageMenuEdit' && toId) {
        this.initEditMode(toId)
      } else if (toName === 'ManageMenuAdd') {
        this.initAddMode(toParentId)
      }
    },
    'menuItemForm.linkType' (newType) {
      if (newType === 'page' && (this.menuItemForm.pathOrUrl || '').startsWith('http')) {
        this.menuItemForm.pathOrUrl = ''
      }
    }
  },

  methods: {
    getHierarchyLabels (item) {
      if (!item) return []
      const labels = []
      let current = item
      while (current) {
        labels.unshift(current.label || '')
        const found = this.findItemById(current.id)
        current = found && found.parent ? found.parent : null
      }
      return labels
    },

    pageTitle (page) {
      if (page.data && page.data.rs && page.data.rs.title) return page.data.rs.title
      if (page.data && page.data.en && page.data.en.title) return page.data.en.title
      return 'Untitled'
    },

    pagePath (page) {
      return page.url ? `/${page.url}` : '/'
    },

    /** Protected menu titles (Serbian and English). When any of these appears as item label, edit/delete are hidden. */
    protectedMenuTitles () {
      return [
        'O nama', 'About Us', 'About',
        'Projekti', 'Projects',
        'Galerija', 'Gallery',
        'Kontakt', 'Contact',
        'Home'
      ]
    },

    isProtectedMenuItem (item) {
      if (!item) return false
      const normalized = (v) => (v && String(v).trim().toLowerCase()) || ''
      const set = new Set(this.protectedMenuTitles().map((t) => t.trim().toLowerCase()))
      const label = normalized(item.label)
      const srb = normalized(item.labelSrb)
      const eng = normalized(item.labelEng)
      return set.has(label) || set.has(srb) || set.has(eng)
    },

    canEditItem (item) {
      return !this.isProtectedMenuItem(item)
    },

    findItemById (id) {
      const search = (list, parent, topLevelIndex) => {
        for (let i = 0; i < list.length; i++) {
          if (list[i].id === id) {
            return { item: list[i], parent, childIndex: i, topLevelIndex }
          }
          if (Array.isArray(list[i].children)) {
            const found = search(list[i].children, list[i], topLevelIndex)
            if (found) return found
          }
        }
        return null
      }
      for (let i = 0; i < this.menuItems.length; i++) {
        if (this.menuItems[i].id === id) {
          return { item: this.menuItems[i], parent: null, childIndex: null, topLevelIndex: i }
        }
        if (Array.isArray(this.menuItems[i].children)) {
          const found = search(this.menuItems[i].children, this.menuItems[i], i)
          if (found) return found
        }
      }
      return null
    },

    initAddMode (parentId) {
      this.formMode = 'add'
      if (parentId) {
        const found = this.findItemById(String(parentId))
        this.addSubItemParent = found ? found.item : null
        this.menuItemForm = {
          ...getDefaultMenuItemForm(),
          isSubmenu: !!found,
          parentId: found ? found.item.id : null
        }
      } else {
        this.addSubItemParent = null
        this.menuItemForm = getDefaultMenuItemForm()
      }
      this.editingItem = null
      this.editingParentIndex = null
      this.editingChildIndex = null
    },

    initEditMode (id) {
      const found = this.findItemById(id)
      if (!found) {
        this.$router.replace({ name: 'Dashboard' })
        return
      }
      this.formMode = 'edit'
      this.addSubItemParent = null
      this.editingItem = found.item
      this.editingParentIndex = found.topLevelIndex
      this.editingChildIndex = found.childIndex
      this.editingParent = found.parent
      const label = found.item.label || ''
      const labelSrb = found.item.labelSrb != null ? found.item.labelSrb : label
      const labelEng = found.item.labelEng != null ? found.item.labelEng : label
      const parentId = found.parent ? found.parent.id : null
      this.menuItemForm = {
        isDropdown: !!found.item.isDropdown,
        isSubmenu: !!found.parent,
        parentId,
        labelSrb,
        labelEng,
        linkType: found.item.linkType || 'page',
        pathOrUrl: found.item.path || '',
        openInNewTab: !!found.item.openInNewTab
      }
    },

    goBack () {
      this.$router.push({ name: 'Dashboard' })
    },

    goToAdd () {
      this.$router.push({ name: 'ManageMenuAdd' })
    },

    goToAddSubItem (parent) {
      this.$router.push({ name: 'ManageMenuAdd', query: { parentId: parent.id } })
    },

    goToEdit (item) {
      this.$router.push({ name: 'ManageMenuEdit', params: { id: item.id } })
    },

    deleteItem (parent, index) {
      if (parent) {
        this.removeMenuItem({ parent, childIndex: index })
      } else {
        this.removeMenuItem({ topLevelIndex: index })
      }
    },

    toggleExpand (id) {
      this.$store.commit('menu/toggleExpand', id)
    },

    discardChanges () {
      this.$store.commit('menu/discardChanges')
    },

    async publishMenu () {
      try {
        await this.$store.dispatch('menu/saveMenu')
        Message.success('Meni je uspešno sačuvan.')
      } catch (_) {
        Message.error('Greška pri čuvanju menija. Proverite konekciju i API.')
      }
    },

    ...mapMutations('menu', ['removeMenuItem']),

    async saveMenuItem () {
      const f = this.menuItemForm
      const labelSrb = (f.labelSrb || '').trim()
      const labelEng = (f.labelEng || '').trim()
      const label = labelSrb || labelEng
      if (!label) return
      const pathOrUrl = (f.pathOrUrl || '').trim()

      if (this.formMode === 'edit' && this.editingItem) {
        const found = this.findItemById(this.editingItem.id)
        if (!found) return
        const updates = {
          label,
          labelSrb: labelSrb || undefined,
          labelEng: labelEng || undefined,
          linkType: f.linkType,
          path: pathOrUrl,
          openInNewTab: f.openInNewTab,
          isDropdown: f.isDropdown,
          children: !f.isDropdown ? null : (Array.isArray(this.editingItem.children) ? this.editingItem.children : [])
        }
        this.$store.commit('menu/updateMenuItem', { item: this.editingItem, updates })
        const targetParentFound = (f.isSubmenu && f.parentId) ? this.findItemById(f.parentId) : null
        const targetParent = targetParentFound ? targetParentFound.item : null
        const currentParent = found.parent
        const sameLocation = (targetParent && currentParent && targetParent.id === currentParent.id) ||
          (!targetParent && !currentParent)
        if (!sameLocation) {
          if (currentParent != null) {
            this.$store.commit('menu/removeMenuItem', { parent: currentParent, childIndex: found.childIndex })
          } else {
            this.$store.commit('menu/removeMenuItem', { topLevelIndex: found.topLevelIndex })
          }
          if (targetParent) {
            this.$store.commit('menu/addSubItem', { parent: targetParent, child: this.editingItem })
          } else {
            this.$store.commit('menu/addMenuItem', this.editingItem)
          }
        }
        try {
          await this.$store.dispatch('menu/saveMenu')
          Message.success('Menu saved successfully.')
          this.goBack()
        } catch (_) {
          Message.error('Failed to save menu. Check connection and API.')
        }
        return
      }

      if (this.formMode === 'add') {
        const parent = this.effectiveParent
        if (f.isSubmenu && !parent) return
        const id = await this.$store.dispatch('menu/newId')
        const newItem = {
          id,
          label,
          labelSrb: labelSrb || undefined,
          labelEng: labelEng || undefined,
          path: pathOrUrl,
          linkType: f.linkType || 'page',
          openInNewTab: f.openInNewTab,
          // Sub-items can be dropdowns too, so the menu nests without a depth limit.
          isDropdown: f.isDropdown,
          children: f.isDropdown ? [] : null
        }
        if (parent) {
          this.$store.commit('menu/addSubItem', { parent, child: newItem })
        } else {
          this.$store.commit('menu/addMenuItem', newItem)
        }
        try {
          await this.$store.dispatch('menu/saveMenu')
          Message.success('Menu saved successfully.')
          this.goBack()
        } catch (_) {
          Message.error('Failed to save menu. Check connection and API.')
        }
      }
    },

    onDragStart (e, parent, index) {
      this.dragSource = { parent, index }
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
    },

    onDragEnd () {
      this.dragSource = null
    },

    onDragOver (e) {
      if (!this.dragSource) return
      e.dataTransfer.dropEffect = 'move'
    },

    onDrop (parent, index) {
      const source = this.dragSource
      this.onDragEnd()
      // Dragging only reorders within one list; moving an item to another
      // level is done through the "Parent menu" field.
      if (!source || source.parent !== parent) return
      this.$store.commit('menu/moveMenuItem', {
        parent,
        fromIndex: source.index,
        toIndex: index
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.manage-menu {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: 'Manrope', sans-serif;
}

.manage-menu__header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(24px);
  padding: 2rem 3rem;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
}

.manage-menu__header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.manage-menu__title {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0;
}

.manage-menu__header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.manage-menu__btn-secondary {
  padding: 0.75rem 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  background: #fff;
  text-decoration: none;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  &:hover {
    background: #f8fafc;
    color: #0f172a;
    border-color: #e2e8f0;
  }
}

.manage-menu__btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #1a1a1a !important;
  color: #fff !important;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: none !important;
  cursor: pointer;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  .material-symbols-outlined {
    font-size: 0.875rem;
  }
  &:hover {
    background: #334155 !important;
    color: #fff !important;
    border-color: transparent !important;
  }
}

.manage-menu--edit .manage-menu__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 2rem 3rem;
  display: flex;
  gap: 2rem;
}

.manage-menu__left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.manage-menu__right {
  flex-shrink: 0;
  width: 400px;
  min-height: 0;
  overflow: auto;
}

.manage-menu__list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: #fff;
  border-radius: 2rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.manage-menu__footer {
  flex-shrink: 0;
  padding: 1rem 3rem 2rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  background: #fff;
}

.manage-menu__footer-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
}

.manage-menu__footer-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #eab308;
  flex-shrink: 0;
}

.manage-menu__footer-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.manage-menu__footer-discard {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem 0;
  &:hover {
    color: #0f172a;
  }
}

.manage-menu__footer-publish {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background: #1a1a1a;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #334155;
  }
}

.manage-menu__panel {
  background: #fff;
  border-radius: 2rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.manage-menu__panel-title {
  margin: 0;
  padding: 1.5rem 1.5rem 1rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  border-bottom: 1px solid #f1f5f9;
}

.manage-menu__panel-body {
  padding: 1rem 1.5rem;
}

.manage-menu__panel-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #f1f5f9;
  .el-button .manage-menu__btn-icon {
    font-size: 1rem;
    vertical-align: middle;
    margin-right: 0.25rem;
  }
}

.manage-menu__btn-save {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #1a1a1a !important;
  color: #fff !important;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: none !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover:not(:disabled) {
    background: #334155 !important;
    color: #fff !important;
    border-color: transparent !important;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.manage-menu__form-group {
  margin-bottom: 1.25rem;
}

.manage-menu__form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.5rem;
}

.manage-menu__form-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.manage-menu__form-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #475569;
  cursor: pointer;
  &--standalone {
    margin-top: 0.25rem;
  }
  input[type='checkbox'] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
  }
}

.manage-menu__form-input,
.manage-menu__form-select {
  display: block;
  width: 100%;
}

.manage-menu__form-select.full-width {
  width: 100%;
}

.manage-menu__parent-fixed {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}
</style>
